import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { isNotNull } from '@/utils/helpers';
import { RootState } from '../../store';
import { postCustomerQuizzesV2List, CustomerQuizzesV2ListRequestBody } from '@/features/Quiz/api/getQuizzes';
import { CustomerQuizV2ListItem } from '@/features/Quiz/types';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';

const PAGE_SIZE = 20;

export const getAllCustomerQuizzesV2 = createAsyncThunk<
    { items: CustomerQuizV2ListItem[]; page: number; total: number; totalPages: number },
    {
        activePage: number;
        activeSearchKeyword?: string;
        isShowExpired: boolean;
    },
    { rejectValue: string }
>('quizzes/getAllCustomerQuizzesV2', async ({ activePage, activeSearchKeyword, isShowExpired }) => {
    const body: CustomerQuizzesV2ListRequestBody = {
        page: activePage - 1,
        size: PAGE_SIZE,
        sort: [['startTime', 'desc']],
        showExpired: isShowExpired,
        ...(activeSearchKeyword?.trim() ? { name: activeSearchKeyword.trim() } : {}),
    };
    const res = await postCustomerQuizzesV2List(body);
    const totalPages = res.size > 0 ? Math.max(1, Math.ceil(res.total / res.size)) : 1;

    return {
        items: res.data ?? [],
        page: activePage,
        total: res.total,
        totalPages,
    };
});

const quizCustomerV2Adapter = createEntityAdapter<CustomerQuizV2ListItem>();

type State = {
    pages: Record<number, CustomerQuizV2ListItem['id'][]>;
    activePage: number;
    total: number;
    totalPages: number;
    isLoading: boolean;
};

const quizCustomerV2Slice = createSlice({
    name: 'quizCustomerV2',
    initialState: quizCustomerV2Adapter.getInitialState<State>({
        pages: {},
        activePage: 1,
        totalPages: 1,
        total: 0,
        isLoading: false,
    }),
    reducers: {
        setQuizCustomerV2ActivePage: (state, action: PayloadAction<number>) => {
            state.activePage = action.payload;
        },
        resetQuizCustomerV2Pages: (state) => {
            state.pages = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAllCustomerQuizzesV2.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getAllCustomerQuizzesV2.fulfilled, (state, action) => {
            quizCustomerV2Adapter.upsertMany(state, action.payload.items);
            state.pages[action.payload.page] = action.payload.items.map((item) => item.id);
            state.total = action.payload.total;
            state.activePage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.isLoading = false;
        });
        builder.addCase(getAllCustomerQuizzesV2.rejected, (state) => {
            state.isLoading = false;
        });
    },
});

export const quizCustomerV2 = quizCustomerV2Slice.reducer;
export const { resetQuizCustomerV2Pages, setQuizCustomerV2ActivePage } = quizCustomerV2Slice.actions;

const selectState = (state: RootState) => state.quizzes.quizCustomerV2;

export const { selectById: selectQuizCustomerV2ById } = quizCustomerV2Adapter.getSelectors(selectState);

const selectRootState = (state: RootState) => state;

const selectIdsByPage = createSelector(
    [selectState, (_: RootState, page: number) => page],
    (state, page) => state.pages[page] ?? []
);

export const selectQuizCustomerV2Page = createSelector(
    [selectRootState, (state: RootState, page: number) => selectIdsByPage(state, page)],
    (state, ids) => ids.map((id) => selectQuizCustomerV2ById(state, id)).filter(isNotNull)
);

export const selectQuizCustomerV2ActivePage = createSelector([selectState], (state) => state.activePage);
export const selectQuizCustomerV2TotalPages = createSelector([selectState], (state) => state.totalPages);
export const selectQuizCustomerV2Loading = createSelector([selectState], (state) => state.isLoading);
